import { Component } from '@theme/component';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import {
  bomStore,
  BomLineKind,
  BomLineStatus,
  BomSaveState,
  BOM_CHANGE_EVENT,
  BOM_SAVE_EVENT,
  openCustomerSignIn,
} from '@theme/bom-store';

/**
 * Number of concurrent predictive-search requests while resolving SKUs.
 * @constant {number}
 */
const RESOLVE_CONCURRENCY = 4;

/**
 * Search fields queried on the suggest.json endpoint.
 * @constant {string}
 */
const SEARCH_FIELDS = 'variants.sku,title';

/**
 * @typedef {object} QuickOrderFormRefs
 * @property {HTMLTextAreaElement} [pasteInput]
 * @property {HTMLInputElement} [fileInput]
 * @property {HTMLInputElement} [skuInput]
 * @property {HTMLInputElement} [qtyInput]
 * @property {HTMLElement} errorContainer
 * @property {HTMLElement} errorText
 * @property {HTMLElement} [workspace]
 * @property {HTMLElement} [partsRegion]
 * @property {HTMLElement} tableWrapper
 * @property {HTMLTableSectionElement} tableBody
 * @property {HTMLTemplateElement} rowTemplate
 * @property {HTMLElement} grandTotal
 * @property {HTMLElement} [headerGrandTotal]
 * @property {HTMLElement} actionsBar
 * @property {HTMLButtonElement} [addToCartButton]
 * @property {HTMLElement} [addToCartButtonText]
 * @property {HTMLElement} [saveStatus]
 * @property {HTMLButtonElement} [shareButton]
 * @property {HTMLElement} [shareFeedback]
 * @property {HTMLElement} [readOnlyBanner]
 * @property {HTMLElement} [inputsPanel]
 * @property {HTMLDetailsElement} [importDetails]
 * @property {HTMLElement} [composeHint]
 * @property {HTMLElement} [lineCount]
 * @property {HTMLInputElement} [titleInput]
 * @property {HTMLElement} [savedDetails]
 * @property {HTMLElement} [savedCount]
 * @property {HTMLElement} [savedEmpty]
 * @property {HTMLUListElement} [savedList]
 * @property {HTMLTemplateElement} [savedItemTemplate]
 * @property {HTMLButtonElement} [newBomButton]
 */

/**
 * Bill of Materials builder — paste/upload/manual SKU entry, sync with
 * bomStore (local + server autosave), share links, and bulk add-to-cart.
 *
 * @extends Component<QuickOrderFormRefs>
 */
class QuickOrderForm extends Component {
  requiredRefs = [
    'errorContainer',
    'errorText',
    'tableWrapper',
    'tableBody',
    'rowTemplate',
    'grandTotal',
    'actionsBar',
  ];

  /** @type {Map<string, import('@theme/bom-store').BomLine>} */
  #rows = new Map();

  /** @type {Set<string>} */
  #resolving = new Set();

  /** @type {string} */
  #currency = 'USD';

  /** @type {string} */
  #moneyFormat = '{{amount}}';

  /** @type {boolean} */
  #syncingFromStore = false;

  /** @type {(() => void)|null} */
  #unsubscribe = null;

  /** @type {Array<{id: string, title: string, lineCount: number, updatedAt: string}>} */
  #savedBoms = [];

  /** @type {string|null} */
  #listedPublicId = null;

  /** @type {'compose'|'review'|null} */
  #layoutMode = null;

  connectedCallback() {
    super.connectedCallback();

    this.#currency = this.dataset.currency ?? 'USD';
    this.#moneyFormat = this.dataset.moneyFormat ?? '{{amount}}';

    document.addEventListener(BOM_SAVE_EVENT, this.#onSaveState);
    this.refs.savedList?.addEventListener('click', this.#onSavedListClick);

    // Hydrate once after init, then listen. Registering beforehand double-hydrates
    // on the init emit and discards in-flight vendor lookups.
    bomStore.init().then(() => {
      if (!this.isConnected) return;
      this.#hydrateFromStore({ applyLayout: true });
      this.#updateChrome();
      this.#refreshSavedList();
      document.addEventListener(BOM_CHANGE_EVENT, this.#onStoreChange);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(BOM_CHANGE_EVENT, this.#onStoreChange);
    document.removeEventListener(BOM_SAVE_EVENT, this.#onSaveState);
    this.refs.savedList?.removeEventListener('click', this.#onSavedListClick);
    this.#resolving.clear();
  }

  get #maxRows() {
    return parseInt(this.dataset.maxRows ?? '', 10) || 200;
  }

  get #readOnly() {
    return bomStore.isShareView || !bomStore.canEdit;
  }

  #onStoreChange = () => {
    if (this.#syncingFromStore) return;
    // External adds (PDP) should appear here without clobbering pending resolves.
    // Keep the current layout mode so the form does not jump mid-task.
    this.#hydrateFromStore({ applyLayout: false });
    this.#updateChrome();
  };

  #onSaveState = () => {
    // Save status only — never re-hydrate here or we re-trigger autosave in a loop.
    this.#updateChrome();
    if (bomStore.saveState !== BomSaveState.SAVED || !bomStore.publicId) return;

    const current = this.#savedBoms.find((bom) => bom.id === bomStore.publicId);
    if (!current) {
      this.#refreshSavedList();
      return;
    }

    current.title = bomStore.title;
    current.lineCount = bomStore.lineCount;
    current.updatedAt = bomStore.draft.updatedAt || current.updatedAt;
    this.#listedPublicId = bomStore.publicId;
    this.#renderSavedList();
  };

  /**
   * @param {{applyLayout?: boolean}} [options]
   */
  #hydrateFromStore(options = {}) {
    const { applyLayout = false } = options;
    this.#syncingFromStore = true;
    try {
      const lines = bomStore.lines;
      const nextIds = new Set();
      for (const line of lines) {
        const key = this.#rowKey(line);
        nextIds.add(key);
        const existing = this.#rows.get(key);
        if (existing) {
          // Keep the same object so in-flight SKU/vendor resolves still write here.
          const vendor = existing.vendor || line.vendor || '';
          const productHandle = existing.productHandle || line.productHandle || '';
          const title = existing.title || line.title || '';
          Object.assign(existing, line, { vendor, productHandle, title });
        } else {
          this.#rows.set(key, line);
        }
      }
      for (const key of [...this.#rows.keys()]) {
        if (!nextIds.has(key)) this.#rows.delete(key);
      }
      if (applyLayout || this.#layoutMode == null) {
        this.#applyLayout(this.#rows.size > 0 ? 'review' : 'compose');
      }
      this.#render();
    } finally {
      this.#syncingFromStore = false;
    }
    this.#resolvePendingRows();
  }

  /**
   * Reorder workspace regions so DOM order matches the active layout mode.
   * Call on hydrate / clear / new / load — not on every row change.
   * @param {'compose'|'review'} mode
   */
  #applyLayout(mode) {
    this.#layoutMode = mode;
    this.dataset.layout = mode;

    const { workspace, inputsPanel, partsRegion, composeHint, importDetails, errorContainer } =
      this.refs;
    if (!workspace || !inputsPanel || !partsRegion || !errorContainer) return;

    const manualPanel = inputsPanel.querySelector('.quick-order-form__manual-panel');

    if (mode === 'review') {
      if (manualPanel instanceof HTMLElement && importDetails) {
        inputsPanel.append(manualPanel, importDetails);
      }
      workspace.append(partsRegion, errorContainer, inputsPanel);
      if (composeHint) composeHint.hidden = true;
      if (importDetails) importDetails.open = false;
    } else {
      if (manualPanel instanceof HTMLElement && importDetails) {
        inputsPanel.append(importDetails, manualPanel);
      }
      const nodes = [inputsPanel];
      if (composeHint) nodes.push(composeHint);
      nodes.push(errorContainer, partsRegion);
      workspace.append(...nodes);
      if (composeHint) composeHint.hidden = this.#rows.size > 0 || this.#readOnly;
      if (importDetails) importDetails.open = true;
    }
  }

  #rowKey(line) {
    return line.id;
  }

  #pushToStore() {
    if (this.#readOnly || this.#syncingFromStore) return;
    this.#syncingFromStore = true;
    try {
      // setLines no-ops when the payload is unchanged, so hydrate→resolve→push
      // cannot keep scheduling remote saves.
      bomStore.setLines([...this.#rows.values()]);
    } finally {
      this.#syncingFromStore = false;
    }
    this.#updateChrome();
  }

  onPasteSubmit(event) {
    event.preventDefault();
    if (this.#readOnly || !this.refs.pasteInput) return;

    const { pasteInput, importDetails } = this.refs;
    this.#addLines(pasteInput.value);
    pasteInput.value = '';
    // Keep import open after paste so batch entry is not dismissed mid-task.
    if (importDetails && this.#layoutMode === 'review') importDetails.open = true;
    pasteInput.focus();
  }

  onUploadClick(event) {
    event.preventDefault();
    if (this.#readOnly) return;
    this.refs.fileInput?.click();
  }

  onFileUpload(event) {
    if (this.#readOnly) return;
    const input = /** @type {HTMLInputElement} */ (event.target);
    const file = input.files?.[0];
    input.value = '';

    if (!file) return;

    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        this.#addLines(reader.result);
        if (this.refs.importDetails && this.#layoutMode === 'review') {
          this.refs.importDetails.open = true;
        }
      } else {
        this.#showError(this.dataset.textFileError);
      }
    });
    reader.addEventListener('error', () => this.#showError(this.dataset.textFileError));
    reader.readAsText(file);
  }

  onManualAdd(event) {
    event.preventDefault();
    if (this.#readOnly || !this.refs.skuInput || !this.refs.qtyInput) return;

    const { skuInput, qtyInput } = this.refs;
    const sku = skuInput.value.trim();
    const quantity = Math.max(1, parseInt(qtyInput.value, 10) || 1);

    if (sku) {
      this.#addRows([{ sku, quantity }]);
    }

    skuInput.value = '';
    qtyInput.value = '1';
    skuInput.focus();
  }

  onQuantityInput(event) {
    if (this.#readOnly) return;
    const input = /** @type {HTMLInputElement} */ (event.target);
    const tableRow = input.closest('tr[data-line-id]');
    const row = tableRow ? this.#rows.get(tableRow.dataset.lineId ?? '') : undefined;

    if (!row || !(tableRow instanceof HTMLElement)) return;

    row.quantity = Math.max(1, parseInt(input.value, 10) || 1);

    const totalCell = tableRow.querySelector('[data-cell="total"]');
    if (totalCell) totalCell.textContent = this.#lineTotal(row);

    this.#renderGrandTotal();
    this.#pushToStore();
  }

  onRemoveRow(event) {
    event.preventDefault();
    if (this.#readOnly) return;

    const tableRow = /** @type {HTMLElement} */ (event.target).closest('tr[data-line-id]');
    if (!tableRow) return;

    this.#rows.delete(tableRow.dataset.lineId ?? '');
    this.#clearError();
    this.#render();
    this.#pushToStore();
  }

  onClearAll(event) {
    event.preventDefault();
    if (this.#readOnly) return;

    this.#rows.clear();
    this.#resolving.clear();
    this.#clearError();
    this.#applyLayout('compose');
    this.#render();
    bomStore.clear();
    this.refs.pasteInput?.focus();
  }

  onTitleInput(event) {
    if (this.#readOnly) return;
    const input = /** @type {HTMLInputElement} */ (event.target);
    bomStore.setTitle(input.value);
  }

  onSignIn(event) {
    event.preventDefault();
    openCustomerSignIn(this.dataset.loginUrl);
  }

  async onNewBom(event) {
    event.preventDefault();
    if (this.refs.newBomButton) this.refs.newBomButton.disabled = true;
    try {
      if (!(await this.#persistCurrentDraft())) return;
      bomStore.startNew();
      this.#applyLayout('compose');
      if (this.refs.titleInput) {
        this.refs.titleInput.value = '';
        this.refs.titleInput.focus();
      }
      await this.#refreshSavedList();
    } finally {
      if (this.refs.newBomButton) this.refs.newBomButton.disabled = false;
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #onSavedListClick = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-action]');
    if (!(button instanceof HTMLButtonElement)) return;
    const item = button.closest('[data-bom-id]');
    const id = item instanceof HTMLElement ? item.dataset.bomId : '';
    if (!id) return;

    if (button.dataset.action === 'delete') {
      const confirmed = window.confirm(
        this.dataset.textDeleteConfirm || 'Delete this BoM? This cannot be undone.',
      );
      if (!confirmed) return;
      try {
        await bomStore.deleteAccountBom(id);
        await this.#refreshSavedList();
      } catch (error) {
        this.#showError(error?.message || this.dataset.textLoadError || 'Could not delete this BoM.');
      }
      return;
    }

    if (button.dataset.action === 'open') {
      if (id === bomStore.publicId) {
        this.refs.savedDetails?.removeAttribute('open');
        return;
      }
      try {
        if (!(await this.#persistCurrentDraft())) return;
        await bomStore.loadAccountBom(id);
        // Hydrate via store change may race; force layout from the loaded draft.
        this.#hydrateFromStore({ applyLayout: true });
        this.refs.savedDetails?.removeAttribute('open');
        this.refs.titleInput?.focus();
        await this.#refreshSavedList();
      } catch (error) {
        this.#showError(error?.message || this.dataset.textLoadError || 'Could not open this BoM.');
      }
    }
  };

  /**
   * @returns {Promise<boolean>}
   */
  async #persistCurrentDraft() {
    if (this.#readOnly) return true;
    this.#pushToStore();
    await bomStore.flush();
    if (bomStore.saveState === BomSaveState.ERROR) {
      this.#showError(bomStore.saveError || this.dataset.textSaveError || 'Could not save BoM.');
      return false;
    }
    return true;
  }

  async #refreshSavedList() {
    if (!this.refs.savedList || !this.refs.savedItemTemplate) return;
    if (!bomStore.customerLoggedIn) return;

    try {
      this.#savedBoms = await bomStore.listAccountBoms();
      this.#listedPublicId = bomStore.publicId;
    } catch (error) {
      console.warn('[quick-order-form] saved BoM list failed:', error);
      this.#showError(error?.message || this.dataset.textListError || 'Could not load saved BoMs.');
      return;
    }

    this.#renderSavedList();
  }

  #renderSavedList() {
    const list = this.refs.savedList;
    const empty = this.refs.savedEmpty;
    const template = this.refs.savedItemTemplate;
    const count = this.refs.savedCount;
    if (!list || !template) return;

    const hasSaved = this.#savedBoms.length > 0;
    if (empty) empty.hidden = hasSaved;
    list.hidden = !hasSaved;

    if (count) {
      count.textContent = String(this.#savedBoms.length);
      count.hidden = !hasSaved;
    }

    if (!hasSaved) {
      list.replaceChildren();
      return;
    }

    const fragment = document.createDocumentFragment();
    const currentId = bomStore.publicId;
    const untitled = this.dataset.textUntitled || 'Untitled BoM';
    const metaTemplate = this.dataset.textUpdatedMeta || '__COUNT__ lines · __DATE__';

    for (const bom of this.#savedBoms) {
      const item = /** @type {HTMLElement} */ (
        template.content.firstElementChild.cloneNode(true)
      );
      item.dataset.bomId = bom.id;
      if (bom.id === currentId) {
        item.dataset.current = 'true';
        const openButton = item.querySelector('[data-action="open"]');
        if (openButton instanceof HTMLButtonElement) {
          openButton.setAttribute('aria-current', 'true');
        }
      }

      const title = item.querySelector('[data-cell="title"]');
      if (title) title.textContent = bom.title?.trim() || untitled;

      const meta = item.querySelector('[data-cell="meta"]');
      if (meta) {
        const date = this.#formatSavedDate(bom.updatedAt);
        meta.textContent = metaTemplate
          .replaceAll('__COUNT__', String(bom.lineCount ?? 0))
          .replaceAll('__DATE__', date);
      }

      fragment.append(item);
    }

    list.replaceChildren(fragment);
  }

  /** @param {string|null|undefined} value */
  #formatSavedDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }

  async onShare(event) {
    event.preventDefault();
    const button = this.refs.shareButton;
    const feedback = this.refs.shareFeedback;
    if (!button) return;

    button.disabled = true;
    try {
      // Ensure current table state is persisted before sharing.
      if (!this.#readOnly) this.#pushToStore();
      const url = await bomStore.ensureShareUrl();
      await navigator.clipboard.writeText(url);
      if (feedback) {
        feedback.textContent = this.dataset.textShareCopied || 'Link copied';
        feedback.hidden = false;
      }
      window.setTimeout(() => {
        if (feedback) feedback.hidden = true;
      }, 2500);
    } catch (error) {
      this.#showError(error?.message || this.dataset.textShareError || 'Could not copy share link.');
    } finally {
      button.disabled = false;
      this.#updateChrome();
    }
  }

  async onAddAllToCart(event) {
    event.preventDefault();

    const items = [...this.#rows.values()]
      .filter(
        (row) =>
          (row.status === BomLineStatus.FOUND || row.status === 'found') &&
          row.variantId != null,
      )
      .map((row) => {
        /** @type {{id: number, quantity: number, properties?: Record<string, string>}} */
        const item = {
          id: /** @type {number} */ (row.variantId),
          quantity: row.quantity,
        };
        if (row.kind === BomLineKind.CONFIGURED && row.properties) {
          item.properties = this.#cartProperties(row.properties);
        }
        return item;
      });

    if (items.length === 0 || this.#isResolving) return;

    this.#clearError();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      }),
    );

    if (this.refs.addToCartButton) this.refs.addToCartButton.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: cartItemComponentsSectionIds.join(','),
        }),
      });

      const data = await response.json();

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: {
              description: data.description,
              errors: data.errors,
            },
          }),
        );

        const cart = await this.#refreshCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: true,
            items: cart.items,
            source: 'quick-order-form',
            itemCount: totalQuantity,
          },
        });

        this.#showError(data.description || data.message || this.dataset.textAddToCartError);
        return;
      }

      const cart = await this.#refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'quick-order-form',
          itemCount: totalQuantity,
          sections: data.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferredEventPromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        }),
      );

      this.#showError(this.dataset.textAddToCartError);
    } finally {
      this.#updateAddToCartState();
    }
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  #addLines(text) {
    const entries = [];

    for (const line of text.split(/\r\n|\r|\n/)) {
      const entry = this.#parseLine(line);
      if (entry) entries.push(entry);
    }

    this.#addRows(entries);
  }

  #parseLine(line) {
    const trimmed = line.trim();
    if (!trimmed) return null;

    let cells;
    if (trimmed.includes('\t')) {
      cells = trimmed.split('\t');
    } else if (trimmed.includes(',')) {
      cells = trimmed.split(',');
    } else if (trimmed.includes(';')) {
      cells = trimmed.split(';');
    } else {
      cells = trimmed.split(/\s+/);
    }

    const sku = (cells[0] ?? '').trim();
    if (!sku) return null;

    const rawQuantity = (cells[1] ?? '').trim();
    if (rawQuantity === '') return { sku, quantity: 1 };
    if (!/^\d+$/.test(rawQuantity)) return null;

    const quantity = parseInt(rawQuantity, 10);
    return quantity > 0 ? { sku, quantity } : null;
  }

  #addRows(entries) {
    if (entries.length === 0 || this.#readOnly) return;

    let truncated = false;
    let hasNewRows = false;

    for (const { sku, quantity } of entries) {
      const existing = [...this.#rows.values()].find(
        (row) =>
          row.kind !== BomLineKind.CONFIGURED &&
          row.sku.trim().toLowerCase() === sku.toLowerCase(),
      );

      if (existing) {
        existing.quantity += quantity;
        continue;
      }

      if (this.#rows.size >= this.#maxRows) {
        truncated = true;
        break;
      }

      const id = crypto.randomUUID?.().replace(/-/g, '').slice(0, 16) || `l${Date.now().toString(36)}`;
      this.#rows.set(id, {
        id,
        kind: BomLineKind.UNRESOLVED,
        sku,
        quantity,
        status: BomLineStatus.PENDING,
        variantId: null,
        title: '',
        vendor: '',
        price: null,
        properties: null,
      });
      hasNewRows = true;
    }

    if (truncated) {
      this.#showError(this.dataset.textMaxRowsError);
    } else {
      this.#clearError();
    }

    this.#render();
    this.#pushToStore();
    if (hasNewRows) this.#resolvePendingRows();
  }

  async #resolvePendingRows() {
    const queue = [...this.#rows.values()].filter((row) => {
      if (this.#resolving.has(row.id)) return false;

      if (row.kind === BomLineKind.CONFIGURED) {
        return !row.vendor && Boolean(row.sku);
      }

      const isPending =
        row.status === BomLineStatus.PENDING || row.status === 'pending';
      const needsVendor =
        !row.vendor &&
        (row.status === BomLineStatus.FOUND ||
          row.status === 'found' ||
          row.status === BomLineStatus.UNAVAILABLE ||
          row.status === 'unavailable') &&
        Boolean(row.sku);

      return isPending || needsVendor;
    });

    for (const row of queue) this.#resolving.add(row.id);
    this.#updateAddToCartState();

    const workers = Array.from({ length: RESOLVE_CONCURRENCY }, async () => {
      let row;
      while ((row = queue.shift())) {
        await this.#resolveRow(row);
        this.#render();
      }
    });

    await Promise.all(workers);
    if (!this.#readOnly && !this.#syncingFromStore) this.#pushToStore();
    this.#updateAddToCartState();
  }

  /**
   * Apply a patch to the live row. Hydrate may replace Map values, so never
   * mutate a stale snapshot captured when the resolve was queued.
   * @param {string} id
   * @param {Partial<import('@theme/bom-store').BomLine>} patch
   * @returns {import('@theme/bom-store').BomLine|null}
   */
  #patchRow(id, patch) {
    const row = this.#rows.get(id);
    if (!row) return null;
    Object.assign(row, patch);
    return row;
  }

  async #resolveRow(row) {
    const id = row.id;
    const enrichOnly =
      row.kind === BomLineKind.CONFIGURED ||
      (!row.vendor &&
        row.status !== BomLineStatus.PENDING &&
        row.status !== 'pending');

    try {
      if (enrichOnly && (row.productHandle || row.vendor)) {
        const vendor = row.vendor || (await this.#lookupVendor(row, null));
        if (vendor) this.#patchRow(id, { vendor });
        if (this.#rows.get(id)?.vendor) return;
      }

      const data = await this.#fetchSuggestions(row.sku);
      const products = data?.resources?.results?.products;
      const { product, variant } = this.#matchCatalogProduct(
        Array.isArray(products) ? products : [],
        row,
      );

      if (!product && !enrichOnly) {
        this.#patchRow(id, { status: BomLineStatus.NOT_FOUND, kind: BomLineKind.UNRESOLVED });
        return;
      }

      const handle = row.productHandle || product?.handle || '';
      if (handle) this.#patchRow(id, { productHandle: handle });

      const vendor = await this.#lookupVendor(this.#rows.get(id) || row, product);
      if (vendor) this.#patchRow(id, { vendor });

      if (enrichOnly) return;
      if (!variant) {
        this.#patchRow(id, { status: BomLineStatus.NOT_FOUND, kind: BomLineKind.UNRESOLVED });
        return;
      }

      const available = variant.available ?? product?.available ?? true;
      this.#patchRow(id, {
        variantId: typeof variant.id === 'number' ? variant.id : Number(variant.id) || null,
        title: this.#variantTitle(product, variant),
        price: convertMoneyToMinorUnits(String(variant.price ?? ''), this.#currency),
        kind: BomLineKind.VARIANT,
        status: available ? BomLineStatus.FOUND : BomLineStatus.UNAVAILABLE,
      });
    } catch (error) {
      console.warn(`[quick-order-form] Failed to resolve SKU "${row.sku}":`, error);
      if (enrichOnly) {
        const vendor = await this.#lookupVendor(this.#rows.get(id) || row, null);
        if (vendor) this.#patchRow(id, { vendor });
      } else {
        this.#patchRow(id, { status: BomLineStatus.NOT_FOUND, kind: BomLineKind.UNRESOLVED });
      }
    } finally {
      this.#resolving.delete(id);
    }
  }

  /**
   * Predictive search often omits variant SKUs. Match by SKU when present,
   * then by variant/product id, handle, or a title that starts with the SKU.
   * @param {object[]} products
   * @param {import('@theme/bom-store').BomLine} row
   */
  #matchCatalogProduct(products, row) {
    if (!Array.isArray(products) || products.length === 0) {
      return { product: null, variant: null };
    }

    const query = (row.sku || '').trim().toLowerCase();
    const handle = (row.productHandle || '').trim().toLowerCase();
    const productId = row.productId != null ? Number(row.productId) : null;
    const variantId = row.variantId != null ? Number(row.variantId) : null;

    for (const product of products) {
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      for (const variant of variants) {
        const sku = typeof variant?.sku === 'string' ? variant.sku.trim().toLowerCase() : '';
        if (query && sku === query) return { product, variant };
        if (variantId && Number(variant?.id) === variantId) return { product, variant };
      }
    }

    for (const product of products) {
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      const onlyVariant = variants.length === 1 ? variants[0] : null;

      if (productId && Number(product?.id) === productId) {
        return { product, variant: onlyVariant };
      }
      if (handle && typeof product?.handle === 'string' && product.handle.toLowerCase() === handle) {
        return { product, variant: onlyVariant };
      }
      if (query && this.#titleMatchesSku(product?.title, query)) {
        return { product, variant: onlyVariant };
      }
    }

    if (query && products.length === 1) {
      const product = products[0];
      const variants = Array.isArray(product?.variants) ? product.variants : [];
      return { product, variant: variants.length === 1 ? variants[0] : null };
    }

    return { product: null, variant: null };
  }

  #titleMatchesSku(title, sku) {
    if (typeof title !== 'string' || !sku) return false;
    const normalized = title.trim().toLowerCase();
    return (
      normalized === sku ||
      normalized.startsWith(`${sku} `) ||
      normalized.startsWith(`${sku}-`) ||
      normalized.startsWith(`${sku} –`) ||
      normalized.startsWith(`${sku} —`)
    );
  }

  /**
   * Prefer vendor from the search hit; otherwise load `/products/{handle}.js`.
   * @param {import('@theme/bom-store').BomLine} row
   * @param {object|null} product
   * @returns {Promise<string>}
   */
  async #lookupVendor(row, product) {
    const fromProduct = typeof product?.vendor === 'string' ? product.vendor.trim() : '';
    if (fromProduct) return fromProduct;

    const handle = row.productHandle || product?.handle || '';
    if (!handle) return '';

    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) return '';
      const data = await response.json();
      return typeof data?.vendor === 'string' ? data.vendor.trim() : '';
    } catch {
      return '';
    }
  }

  async #fetchSuggestions(sku) {
    const url = new URL(`${Theme.routes.predictive_search_url}.json`, window.location.origin);
    url.searchParams.set('q', sku);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', SEARCH_FIELDS);

    for (let attempt = 0; attempt < 2; attempt++) {
      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (response.status === 429 && attempt === 0) {
        const retryAfter = Number(response.headers.get('Retry-After')) || 1;
        await new Promise((resolve) => setTimeout(resolve, retryAfter * 1000));
        continue;
      }

      if (!response.ok) throw new Error(`Suggestion request failed: ${response.status}`);

      return response.json();
    }

    throw new Error('Suggestion request failed: throttled');
  }

  /**
   * Shopify caps line-item property values; split long values into key, key2…
   * chunks so configured BoM lines survive cart add + draft-order checkout.
   * @param {Record<string, string>} properties
   * @returns {Record<string, string>}
   */
  #cartProperties(properties) {
    const chunkSize = 250;
    /** @type {Record<string, string>} */
    const out = {};
    for (const [key, value] of Object.entries(properties)) {
      if (typeof value !== 'string') continue;
      if (value.length <= chunkSize) {
        out[key] = value;
        continue;
      }
      for (let i = 0, chunk = 1; i < value.length; i += chunkSize, chunk++) {
        out[chunk === 1 ? key : `${key}${chunk}`] = value.slice(i, i + chunkSize);
      }
    }
    return out;
  }

  #variantTitle(product, variant) {
    const productTitle = typeof product?.title === 'string' ? product.title : '';
    const variantTitle = typeof variant?.title === 'string' ? variant.title : '';

    if (variantTitle && variantTitle !== 'Default Title' && variantTitle !== productTitle) {
      return `${productTitle} — ${variantTitle}`;
    }

    return productTitle || variantTitle;
  }

  #render() {
    const hasRows = this.#rows.size > 0;
    const readOnly = this.#readOnly;

    this.refs.tableWrapper.hidden = !hasRows;
    this.refs.actionsBar.hidden = !hasRows;
    if (this.refs.lineCount) this.refs.lineCount.textContent = String(this.#rows.size);
    if (this.refs.composeHint) {
      this.refs.composeHint.hidden =
        this.#layoutMode !== 'compose' || hasRows || readOnly;
    }

    for (const editOnly of this.querySelectorAll('[data-edit-only]')) {
      if (editOnly instanceof HTMLElement) {
        editOnly.hidden = readOnly;
        if (editOnly instanceof HTMLButtonElement) editOnly.disabled = readOnly;
      }
    }

    if (this.refs.readOnlyBanner) this.refs.readOnlyBanner.hidden = !readOnly;

    const fragment = document.createDocumentFragment();

    for (const [key, row] of this.#rows) {
      const tableRow = /** @type {HTMLElement} */ (
        this.refs.rowTemplate.content.firstElementChild.cloneNode(true)
      );
      tableRow.dataset.lineId = key;
      tableRow.dataset.status = row.status;
      tableRow.dataset.kind = row.kind;

      const displaySku =
        row.kind === BomLineKind.CONFIGURED
          ? row.properties?._catalogNumber || row.sku
          : row.sku;

      this.#setCellText(tableRow, 'sku', displaySku || '—');
      this.#setCellText(tableRow, 'title', row.title || '—');
      this.#setCellText(tableRow, 'vendor', row.vendor || '—');
      this.#setCellText(tableRow, 'price', row.price != null ? this.#formatMoney(row.price) : '—');
      this.#setCellText(tableRow, 'total', this.#lineTotal(row));

      const statusCell = tableRow.querySelector('[data-cell="status"]');
      if (statusCell) {
        const badge = document.createElement('span');
        badge.className = `quick-order-form__status quick-order-form__status--${row.status}`;
        badge.textContent = this.#statusText(row.status);
        statusCell.append(badge);
      }

      const quantityInput = tableRow.querySelector('[data-cell="quantity"]');
      if (quantityInput instanceof HTMLInputElement) {
        quantityInput.value = String(row.quantity);
        quantityInput.disabled =
          readOnly ||
          row.status === BomLineStatus.PENDING ||
          row.status === BomLineStatus.NOT_FOUND;
      }

      const removeButton = tableRow.querySelector('[data-cell="remove"], .quick-order-form__remove');
      if (removeButton instanceof HTMLButtonElement) {
        removeButton.hidden = readOnly;
        removeButton.disabled = readOnly;
      }

      fragment.append(tableRow);
    }

    this.refs.tableBody.replaceChildren(fragment);

    this.#renderGrandTotal();
    this.#updateAddToCartState();
  }

  #updateChrome() {
    const saveStatus = this.refs.saveStatus;
    if (saveStatus) {
      const state = bomStore.saveState;
      let text = '';
      if (this.#readOnly) {
        text = this.dataset.textReadOnly || 'Viewing a shared BoM (read-only)';
      } else if (state === BomSaveState.SAVING || state === BomSaveState.DIRTY) {
        text = this.dataset.textSaving || 'Saving…';
      } else if (state === BomSaveState.SAVED && bomStore.publicId) {
        text = bomStore.customerLoggedIn
          ? this.dataset.textSavedToAccount || 'Saved to your account'
          : this.dataset.textSaved || 'Saved';
      } else if (state === BomSaveState.ERROR) {
        text = bomStore.saveError || this.dataset.textSaveError || 'Save failed';
      }
      saveStatus.textContent = text;
      saveStatus.hidden = !text;
      saveStatus.dataset.state = state;
    }

    const titleInput = this.refs.titleInput;
    if (titleInput instanceof HTMLInputElement) {
      titleInput.disabled = this.#readOnly;
      if (document.activeElement !== titleInput) {
        titleInput.value = bomStore.title;
      }
    }

    if (this.refs.shareButton) {
      this.refs.shareButton.disabled = this.#rows.size === 0 && !bomStore.publicId;
    }

    if (this.refs.readOnlyBanner) this.refs.readOnlyBanner.hidden = !this.#readOnly;

    for (const editOnly of this.querySelectorAll('[data-edit-only]')) {
      if (editOnly instanceof HTMLElement) {
        editOnly.hidden = this.#readOnly;
        if (editOnly instanceof HTMLButtonElement) editOnly.disabled = this.#readOnly;
      }
    }

    if (this.refs.composeHint) {
      this.refs.composeHint.hidden =
        this.#layoutMode !== 'compose' || this.#rows.size > 0 || this.#readOnly;
    }

    this.#renderSavedList();
  }

  #setCellText(tableRow, cell, text) {
    const element = tableRow.querySelector(`[data-cell="${cell}"]`);
    if (element) element.textContent = text;
  }

  #renderGrandTotal() {
    let total = 0;

    for (const row of this.#rows.values()) {
      if (
        (row.status === BomLineStatus.FOUND || row.status === 'found') &&
        row.price != null
      ) {
        total += row.price * row.quantity;
      }
    }

    const formatted = this.#formatMoney(total);
    this.refs.grandTotal.textContent = formatted;
    if (this.refs.headerGrandTotal) this.refs.headerGrandTotal.textContent = formatted;
  }

  #updateAddToCartState() {
    if (!this.refs.addToCartButton) return;

    const hasAddableRows = [...this.#rows.values()].some(
      (row) =>
        (row.status === BomLineStatus.FOUND || row.status === 'found') &&
        row.variantId != null,
    );

    this.refs.addToCartButton.disabled = !hasAddableRows || this.#isResolving;
    if (this.refs.addToCartButtonText) {
      this.refs.addToCartButtonText.textContent = this.#isResolving
        ? this.dataset.textResolving ?? ''
        : this.dataset.textAddAll ?? '';
    }
  }

  get #isResolving() {
    if (this.#resolving.size > 0) return true;

    for (const row of this.#rows.values()) {
      if (row.status === BomLineStatus.PENDING || row.status === 'pending') return true;
    }

    return false;
  }

  #statusText(status) {
    switch (status) {
      case BomLineStatus.FOUND:
      case 'found':
        return this.dataset.textStatusFound ?? status;
      case BomLineStatus.UNAVAILABLE:
      case 'unavailable':
        return this.dataset.textStatusUnavailable ?? status;
      case BomLineStatus.NOT_FOUND:
      case 'not-found':
        return this.dataset.textStatusNotFound ?? status;
      default:
        return this.dataset.textStatusPending ?? status;
    }
  }

  #lineTotal(row) {
    if (
      (row.status !== BomLineStatus.FOUND && row.status !== 'found') ||
      row.price == null
    ) {
      return '—';
    }

    return this.#formatMoney(row.price * row.quantity);
  }

  #formatMoney(value) {
    return formatMoney(value, this.#moneyFormat, this.#currency);
  }

  #showError(message) {
    this.refs.errorText.textContent = message ?? '';
    this.refs.errorContainer.classList.remove('hidden');
  }

  #clearError() {
    this.refs.errorContainer.classList.add('hidden');
  }
}

if (!customElements.get('quick-order-form')) {
  customElements.define('quick-order-form', QuickOrderForm);
}
